import { takeEvery, all, put, call, cancelled } from 'redux-saga/effects'
import axios from 'portal/utils/axios-msal'
import moment from 'moment'

import {
  HOME_ENABLED_ALERT_RULES_COUNT_LOAD,
  HOME_RECORDS_COUNT_LOAD,
  HOME_ALERTS_SUMMARY_LOAD,
  HOME_DESIGN_MATCHES_LOAD,
  LoadAlertsSummaryAction,
  loadAlertsSummaryFailure,
  loadAlertsSummarySuccess,
  LoadEnabledAlertRulesCountAction,
  loadEnabledAlertRulesCountSuccess,
  loadEnabledAlertRulesCountFailure,
  LoadRecordsCountAction,
  loadRecordsCountSuccess,
  loadRecordsCountFailure,
  LoadDesignMatchesAction,
  loadDesignMatchesSuccess,
  loadDesignMatchesFailure
} from './actions'
import { computeFilterParams } from 'portal/store/records/filterSortParams/filterSortParams'
import { RECORD_STATUS } from 'portal/lib/constants'
import queryString from 'query-string'

function* loadRecordsCountSaga(action: LoadRecordsCountAction) {
  const { accountId, projectId, moduleName, period, filterState } = action
  let recordsCount = 0
  const abortController = new AbortController()
  try {
    const statusFilter = `${RECORD_STATUS.PUBLISHED},${RECORD_STATUS.AUTOPUBLISHED}`

    // Apply additional date filters based on the period
    const dateRange: {
      startDate: moment.Moment | null
      endDate: moment.Moment
    } = {
      startDate: null,
      endDate: moment().endOf('day')
    }
    if (period === 'today') {
      dateRange.startDate = moment().startOf('day')
    } else if (period === 'last30Days') {
      dateRange.startDate = moment().subtract(30, 'days').startOf('day')
    }

    if (moduleName === 'delivery' && dateRange.startDate) {
      filterState['deliveryDate'] = dateRange
    } else if (moduleName === 'waste' && dateRange.startDate) {
      filterState['removalDate'] = dateRange
    }

    const response = yield call(() =>
      axios({
        method: 'GET',
        url: `/accounts/${accountId}/projects/${projectId}/modules/${moduleName}/records/records-count`,
        signal: abortController.signal,
        params: {
          'page[size]': 1,
          'ReturnCountOnly': true,
          'filter[status]': statusFilter,
          'filter[DisableAutoFilterForAssigneePermissions]':
            filterState?.assignedTo ? undefined : true,
          ...computeFilterParams(filterState)
        },
        paramsSerializer: params => {
          return queryString.stringify(params)
        },
        validateStatus: status => status === 200
      })
    )
    recordsCount = response.data
    yield put(loadRecordsCountSuccess(moduleName, period, recordsCount))
  } catch (error) {
    yield put(loadRecordsCountFailure(moduleName, period, error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

function* loadEnabledAlertRulesCountSaga(
  action: LoadEnabledAlertRulesCountAction
) {
  const { accountId, projectId } = action
  let recordsCount = 0
  const abortController = new AbortController()
  try {
    const response = yield call(() =>
      axios({
        method: 'GET',
        url: `/accounts/${accountId}/projects/${projectId}/alertrules/enabled-count`,
        signal: abortController.signal,
        params: {
          'page[size]': 1
        },
        validateStatus: status => status === 200
      })
    )
    recordsCount = response.data

    yield put(loadEnabledAlertRulesCountSuccess(recordsCount))
  } catch (error) {
    yield put(loadEnabledAlertRulesCountFailure(error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

function* loadAlertsSummarySaga(action: LoadAlertsSummaryAction) {
  const { accountId, projectId } = action
  let recordsCount = 0
  const abortController = new AbortController()
  try {
    const response = yield call(() =>
      axios({
        method: 'GET',
        url: `/accounts/${accountId}/projects/${projectId}/alerts/summary`,
        signal: abortController.signal,
        params: {
          'page[size]': 1
        },
        validateStatus: status => status === 200
      })
    )
    recordsCount = response.data
    yield put(loadAlertsSummarySuccess(recordsCount))
  } catch (error) {
    yield put(loadAlertsSummaryFailure(error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

// Design Matches Saga
function* loadDesignMatchesSaga(action: LoadDesignMatchesAction) {
  const { accountId, projectId } = action
  const abortController = new AbortController()
  try {
    const response = yield call(() =>
      axios({
        method: 'GET',
        url: `/accounts/${accountId}/projects/${projectId}/modules/delivery/records/designmatchesv2/summary`,
        signal: abortController.signal,
        validateStatus: status => status === 200
      })
    )
    const data = response.data
    yield put(loadDesignMatchesSuccess(data))
  } catch (error) {
    yield put(loadDesignMatchesFailure(error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

export function* watch() {
  yield all([takeEvery(HOME_RECORDS_COUNT_LOAD, loadRecordsCountSaga)])
  yield all([
    takeEvery(
      HOME_ENABLED_ALERT_RULES_COUNT_LOAD,
      loadEnabledAlertRulesCountSaga
    )
  ])
  yield all([takeEvery(HOME_ALERTS_SUMMARY_LOAD, loadAlertsSummarySaga)])
  yield all([takeEvery(HOME_DESIGN_MATCHES_LOAD, loadDesignMatchesSaga)])
}
